import { DEFAULT_ORDER_DOMAINS } from '@/contexts/new-order-context';
import {
  NewOrder_OrderContextQuery,
  NewOrder_OrderContextQueryVariables,
} from '@/types';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineOrderContextAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { ORDER_CONTEXT_QUERY } from '@/contexts/new-order-context-queries';
import { print as printQuery } from 'graphql';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useLazyQuery } from '@/hooks/use-lazy-query';

/**
 * Custom hook to download and save the order context for a specific restaurant and supplier.
 *
 * This hook uses a lazy query to fetch the order context from the server and then saves it
 * locally using the OfflineOrderContextAdapter.
 *
 * @returns {Object} - An object containing the downloadOrderContext function.
 *
 * @function
 * @name useDownloadOrderContext
 *
 * @example
 * const { downloadOrderContext } = useDownloadOrderContext();
 *
 * // Download and save order context for a specific restaurant and supplier
 * downloadOrderContext('restaurant-uuid', 'supplier-uuid');
 */
export const useDownloadOrderContext = () => {
  const getOrderContext = useLazyQuery<
    NewOrder_OrderContextQuery,
    NewOrder_OrderContextQueryVariables
  >(ORDER_CONTEXT_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'network-only',
  });

  /**
   * Fetches and saves the order context for a given restaurant and supplier.
   *
   * @async
   * @param {string} restaurantUUID - The UUID of the restaurant.
   * @param {string} supplierUUID - The UUID of the supplier.
   */
  const downloadOrderContext = async (
    restaurantUUID: string,
    supplierUUID: string,
  ) => {
    const variables: NewOrder_OrderContextQueryVariables = {
      employeeUUID: null,
      includeActiveOrder: false,
      orderDomains: DEFAULT_ORDER_DOMAINS,
      restaurantUUID,
      supplierUUID,
      unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
    };

    const { data: orderContextData } = await getOrderContext({
      context: {
        offlineSync: true,
      },
      variables,
    });
    const offlineOrderContextAdapter = OfflineOrderContextAdapter.getInstance(
      restaurantUUID,
      supplierUUID,
    );
    await offlineOrderContextAdapter.save({
      newOrderContext: orderContextData,
      synced: true,
    });

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(ORDER_CONTEXT_QUERY),
        syncType: OfflineSyncType.OrderContext,
        variables,
      },
    );
  };

  return { downloadOrderContext };
};
